import { useState, useEffect } from "react";
import { Printer, Download, ExternalLink, CheckCircle, XCircle, RefreshCw, Wifi, WifiOff, TestTube } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { connectQZTray, disconnectQZTray, getPrinters, printReceipt, isQZConnected } from "@/lib/qz-tray";
import { toast } from "sonner";

export function QZTrayConfigCard() {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [printers, setPrinters] = useState<string[]>([]);
  const [isPrintingTest, setIsPrintingTest] = useState<string | null>(null);

  useEffect(() => {
    // Check initial connection status
    setIsConnected(isQZConnected());
  }, []);

  const handleConnect = async () => {
    setIsConnecting(true);
    try {
      const connected = await connectQZTray();
      setIsConnected(connected);
      
      if (connected) {
        const availablePrinters = await getPrinters();
        setPrinters(availablePrinters);
        toast.success("Conectado ao QZ Tray!");
      } else {
        toast.error("Não foi possível conectar ao QZ Tray. Verifique se está instalado e rodando.");
      }
    } catch (error) {
      console.error("Error connecting to QZ Tray:", error);
      toast.error("Erro ao conectar ao QZ Tray");
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnectQZTray();
      setIsConnected(false);
      setPrinters([]);
      toast.success("Desconectado do QZ Tray");
    } catch (error) {
      console.error("Error disconnecting:", error);
    }
  };

  const handleRefreshPrinters = async () => {
    if (!isConnected) return;
    
    try {
      const availablePrinters = await getPrinters();
      setPrinters(availablePrinters);
      toast.success(`${availablePrinters.length} impressora(s) encontrada(s)`);
    } catch (error) {
      console.error("Error refreshing printers:", error);
      toast.error("Erro ao buscar impressoras");
    }
  };

  const handleTestPrint = async (printerName: string) => {
    setIsPrintingTest(printerName);
    
    const testContent = `
================================
      TESTE DE IMPRESSAO
================================
Impressora: ${printerName}
Data: ${new Date().toLocaleDateString("pt-BR")}
Hora: ${new Date().toLocaleTimeString("pt-BR")}
--------------------------------
Se voce esta lendo isso,
a impressao esta funcionando!
================================


`;

    try {
      const success = await printReceipt(printerName, testContent);
      if (success) {
        toast.success(`Teste enviado para ${printerName}`);
      }
    } catch (error) {
      console.error("Error printing test:", error);
      toast.error("Erro ao imprimir teste");
    } finally {
      setIsPrintingTest(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Printer className="h-5 w-5" />
          QZ Tray - Impressão Térmica
        </CardTitle>
        <CardDescription>
          Configure a impressão direta em impressoras térmicas usando QZ Tray
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Status da Conexão */}
        <div className="flex items-center justify-between rounded-lg border p-4">
          <div className="flex items-center gap-3">
            {isConnected ? (
              <Wifi className="h-5 w-5 text-green-500" />
            ) : (
              <WifiOff className="h-5 w-5 text-muted-foreground" />
            )}
            <div>
              <p className="font-medium">Status da Conexão</p>
              <p className="text-sm text-muted-foreground">
                {isConnected ? "Conectado ao QZ Tray" : "Não conectado"}
              </p>
            </div>
          </div>
          <Badge variant={isConnected ? "default" : "secondary"}>
            {isConnected ? (
              <><CheckCircle className="h-3 w-3 mr-1" /> Online</>
            ) : (
              <><XCircle className="h-3 w-3 mr-1" /> Offline</>
            )}
          </Badge>
        </div>

        {/* Botões de Ação */}
        <div className="flex gap-2">
          {!isConnected ? (
            <Button onClick={handleConnect} disabled={isConnecting}>
              {isConnecting ? (
                <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Conectando...</>
              ) : (
                <><Wifi className="h-4 w-4 mr-2" /> Conectar ao QZ Tray</>
              )}
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={handleRefreshPrinters}>
                <RefreshCw className="h-4 w-4 mr-2" /> Atualizar Impressoras
              </Button>
              <Button variant="ghost" onClick={handleDisconnect}>
                Desconectar
              </Button>
            </>
          )}
        </div>

        {/* Lista de Impressoras */}
        {isConnected && (
          <>
            <Separator />
            <div className="space-y-3">
              <h4 className="font-medium flex items-center gap-2">
                <Printer className="h-4 w-4" />
                Impressoras Disponíveis ({printers.length})
              </h4>
              
              {printers.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Nenhuma impressora encontrada. Verifique se suas impressoras estão instaladas.
                </p>
              ) : (
                <div className="space-y-2">
                  {printers.map((printer) => (
                    <div
                      key={printer}
                      className="flex items-center justify-between rounded-lg border p-3"
                    >
                      <div className="flex items-center gap-2">
                        <Printer className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm font-medium">{printer}</span>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleTestPrint(printer)}
                        disabled={isPrintingTest !== null}
                      >
                        {isPrintingTest === printer ? (
                          <><RefreshCw className="h-3 w-3 mr-1 animate-spin" /> Imprimindo...</>
                        ) : (
                          <><TestTube className="h-3 w-3 mr-1" /> Testar</>
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        <Separator />

        {/* Instruções de Instalação */}
        <div className="space-y-4">
          <h4 className="font-medium">Como Configurar</h4>
          
          <div className="space-y-3 text-sm">
            <div className="flex gap-3">
              <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">
                1
              </div>
              <div>
                <p className="font-medium">Baixe o QZ Tray</p>
                <p className="text-muted-foreground">
                  Acesse o site oficial e baixe a versão para seu sistema operacional.
                </p>
                <Button
                  variant="link"
                  className="h-auto p-0 text-primary"
                  onClick={() => window.open("https://qz.io/download/", "_blank")}
                >
                  <Download className="h-3 w-3 mr-1" />
                  Download QZ Tray
                  <ExternalLink className="h-3 w-3 ml-1" />
                </Button>
              </div>
            </div>

            <div className="flex gap-3">
              <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">
                2
              </div>
              <div>
                <p className="font-medium">Instale e Execute</p>
                <p className="text-muted-foreground">
                  Após instalar, execute o QZ Tray. Um ícone aparecerá na bandeja do sistema.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">
                3
              </div>
              <div>
                <p className="font-medium">Conecte ao Sistema</p>
                <p className="text-muted-foreground">
                  Clique em "Conectar ao QZ Tray" acima. Na primeira vez, uma janela pedirá permissão.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">
                4
              </div>
              <div>
                <p className="font-medium">Autorize a Conexão</p>
                <p className="text-muted-foreground">
                  Quando aparecer o prompt de confirmação, marque "Lembrar esta decisão" e clique em "Permitir".
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">
                5
              </div>
              <div>
                <p className="font-medium">Teste a Impressão</p>
                <p className="text-muted-foreground">
                  Selecione uma impressora na lista e clique em "Testar" para verificar se está funcionando.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Dicas */}
        <div className="rounded-lg bg-muted/50 p-4 space-y-2">
          <h5 className="font-medium text-sm">💡 Dicas Importantes</h5>
          <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
            <li>O QZ Tray deve estar rodando sempre que usar o sistema de impressão.</li>
            <li>Configure o QZ Tray para iniciar automaticamente com o Windows.</li>
            <li>Use impressoras térmicas de 80mm para melhor compatibilidade.</li>
            <li>Verifique se o driver da impressora está instalado corretamente.</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}
